import { get, map } from 'lodash-es'

/**
 * GenerateSlug 类用于根据对象字段生成唯一的 slug 字符串，并支持反向解析。
 * @template T 传入的字段对象类型
 */
export default class GenerateSlug<T extends Record<string, any>> {
  /**
   * 存储字段对象
   */
  protected fields: T

  /**
   * 构造函数，初始化字段对象
   * @param fields 字段对象
   */
  constructor(fields: T) {
    this.fields = fields
  }

  /**
   * 对字符串进行格式化，去除特殊字符并转为小写
   * @param str 输入字符串/数字/布尔值
   * @param isValue 是否为值（true 时去除所有非字母数字，false 时用 - 替换）
   * @returns 格式化后的字符串
   */
  private formatString(str: string | number | boolean): string {
    return String(str)
      .replace(/[^a-z0-9-]+/gi, '-')
      .replace(/-{2,}/g, '-')
      .toLowerCase()
  }

  /**
   * 将字段名和字段值拼接为字符串，格式为 name-value
   * @param name 字段名
   * @returns 拼接后的字符串
   */
  private fieldToString(name: keyof T): string {
    const value = this.fields[name]

    // 跳过 symbol 类型、undefined、null、空字符串
    if (
      typeof name === 'symbol' ||
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return ''
    }
    let val: string
    if (Array.isArray(value)) {
      // 数组类型，格式化每一项后用逗号拼接
      val = value.map((item: string) => this.formatString(item)).join(',')
    } else {
      // 其他类型直接格式化
      val = this.formatString(value)
    }
    return [this.formatString(name), val].join('_')
  }

  /**
   * 设置字段值
   * @param name 字段名
   * @param value 字段值
   * @returns this
   */
  setVal<K extends keyof T>(name: K, value: T[K]) {
    if (Object.prototype.hasOwnProperty.call(this.fields, name)) {
      this.fields[name] = value
    }
    return this
  }

  /**
   * 获取字段值
   * @param name 字段名
   * @returns 字段值
   */
  getVal<K extends keyof T>(name: K): T[K] {
    return this.fields[name]
  }

  /**
   * 生成 slug 字符串，字段间用下划线分隔
   * @returns 生成的 slug 字符串
   */
  getSlug(): string {
    const keys = Object.keys(this.fields).sort()
    // key = page 放到最后
    if (keys.includes('page')) {
      keys.push(keys.splice(keys.indexOf('page'), 1)[0])
    }
    const result: string[] = map(keys, (key) => {
      return this.fieldToString(key)
    }).filter(Boolean)
    return result.join('_')
  }

  /**
   * 解析 slug 字符串，反向填充到 fields 对象
   * @param slug 形如 name1-value1_name2-value2 的字符串
   * @returns this
   */
  fromSlug(slug: string) {
    const parts = slug.split('_')
    for (let i = 0; i < parts.length; i++) {
      const key = get(parts, i)
      const value = get(parts, i + 1)
      if (Object.prototype.hasOwnProperty.call(this.fields, key)) {
        i++
        if (typeof this.fields[key] === 'string') {
          // 字符串类型
          ;(this.fields as any)[key] = value
        } else if (Array.isArray(this.fields[key])) {
          // 数组类型
          ;(this.fields as any)[key] = value.split(',').map((v) => v.trim())
        } else if (typeof this.fields[key] === 'number') {
          // 数字类型
          ;(this.fields as any)[key] = parseInt(value, 10)
        } else if (typeof this.fields[key] === 'boolean') {
          // 布尔类型
          ;(this.fields as any)[key] =
            value === 'true' || value === '1' || value === 'on'
        } else {
          // 可能是数字
          if (!isNaN(Number(value))) {
            ;(this.fields as any)[key] = Number(value)
          } else {
            // 不支持的类型
            console.warn(`Unsupported type for field ${key}:`, this.fields[key])
          }
        }
      }
    }
    return this
  }
}
